import createHttp from '../lib/http'

const BROWSER_KEY = 'staff_google_browser_key'
const COMPLETION_KEY = 'staff_google_completion'
const REAUTH_KEY = 'staff_google_reauth'
const RESUME_KEY = 'staff_google_resume'

export function staffGoogleBrowserKey() {
  let key = ''
  try {
    key = sessionStorage.getItem(BROWSER_KEY) || ''
  } catch {
    key = ''
  }
  if (key.length >= 32 && key.length <= 200) return key
  const bytes = new Uint8Array(32)
  window.crypto.getRandomValues(bytes)
  key = window.btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  sessionStorage.setItem(BROWSER_KEY, key)
  return key
}

export async function staffGoogleEnabled() {
  const res = await createHttp.get('api/auth/google/config')
  return res?.status === 200 && res?.data?.enabled === true
}

export async function staffGoogleMethods() {
  const res = await createHttp.get('api/auth/google/methods')
  if (res?.status !== 200 || !res.data) return null
  return res.data
}

export function rememberStaffGoogleResume(path, action) {
  sessionStorage.setItem(RESUME_KEY, JSON.stringify({ path, action }))
}

export function readStaffGoogleResume() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(RESUME_KEY) || 'null')
    if (!raw || typeof raw.path !== 'string') return null
    return raw
  } catch {
    return null
  }
}

export function stashStaffGoogleReauth(code, action) {
  sessionStorage.setItem(REAUTH_KEY, JSON.stringify({ code, action }))
}

export function readStaffGoogleReauth() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(REAUTH_KEY) || 'null')
    if (!raw || typeof raw.code !== 'string') return { code: '', action: '' }
    return { code: raw.code, action: raw.action || '' }
  } catch {
    return { code: '', action: '' }
  }
}

export function clearStaffGoogleReauth() {
  sessionStorage.removeItem(REAUTH_KEY)
}

export function stashStaffGoogleCompletion(payload) {
  sessionStorage.setItem(COMPLETION_KEY, JSON.stringify(payload))
}

export function readStaffGoogleCompletion() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(COMPLETION_KEY) || 'null')
    if (!raw || typeof raw.completion_code !== 'string') return null
    return raw
  } catch {
    return null
  }
}

export function clearStaffGoogleCompletion() {
  sessionStorage.removeItem(COMPLETION_KEY)
}

export async function beginStaffGoogle(purpose, options = {}) {
  const browser_key = staffGoogleBrowserKey()
  const authenticated = Boolean(options.authenticated)
  const path = !authenticated ? 'api/auth/google/start' : purpose === 'link' ? 'api/auth/google/link/start' : 'api/auth/google/reauth/start'
  const body = authenticated ? { browser_key } : { purpose, browser_key }
  const res = await createHttp.post(path, body)
  const url = res?.data?.authorization_url
  if (!url) {
    const error = new Error(res?.data?.message || 'Google sign-in is unavailable.')
    error.code = res?.data?.code || res?.data?.error || 'staff_google_unavailable'
    throw error
  }
  window.location.assign(url)
}

export async function exchangeStaffGoogle(code, purpose) {
  const browser_key = staffGoogleBrowserKey()
  const path = purpose === 'link' ? 'api/auth/google/link/exchange' : purpose === 'reauth' ? 'api/auth/google/reauth/exchange' : 'api/auth/google/exchange'
  return createHttp.post(path, { code, browser_key })
}

export async function registerStaffGoogle(body) {
  return createHttp.post('api/auth/google/register', {
    ...body,
    browser_key: staffGoogleBrowserKey(),
  })
}

export async function unlinkStaffGoogle(password) {
  return createHttp.delete('api/auth/google', {
    data: { password },
  })
}

export async function createStaffGooglePassword(reauthCode, password, passwordConfirmation) {
  return createHttp.post('api/auth/google/password', {
    reauth_code: reauthCode,
    browser_key: staffGoogleBrowserKey(),
    password,
    password_confirmation: passwordConfirmation,
  })
}

const CODE_MESSAGES = {
  google_link_required: ['staff_google_link_required', 'This email already has an account. Sign in with your current method, then connect Google from your profile.'],
  google_code_expired: ['staff_google_code_expired', 'This Google sign-in expired. Start again.'],
  google_browser_mismatch: ['staff_google_browser_mismatch', 'Start Google sign-in again from this browser.'],
  google_identity_in_use: ['staff_google_identity_in_use', 'This Google account is already connected to another user.'],
  google_email_mismatch: ['staff_google_email_mismatch', 'The Google account email must match this user.'],
  last_login_method: ['staff_google_last_method', 'Create a password before disconnecting Google. This is the only way into the account.'],
  wrong_password: ['staff_google_wrong_password', 'The password is incorrect.'],
  active_account_exists: ['staff_google_account_exists', 'An account with this email already exists. Sign in with your current method.'],
  email_verification_required: ['staff_google_verify_email', 'Verify your email before signing in.'],
  account_suspended: ['staff_google_suspended', 'This account is suspended.'],
  google_not_linked: ['staff_google_not_linked', 'Connect Google before continuing.'],
  google_auth_time_required: ['staff_google_auth_time_required', 'Google did not confirm when this account last signed in.'],
  google_reauth_stale: ['staff_google_reauth_stale', 'Google did not report a recent sign-in. Sign in to Google again, then retry.'],
  staff_google_unavailable: ['staff_google_unavailable', 'Google sign-in is unavailable.'],
}

export function staffGoogleMessage(trans, payload) {
  const code = payload?.code || payload?.error || ''
  const known = CODE_MESSAGES[code]
  if (known) {
    return trans({ id: known[0], defaultMessage: known[1] })
  }
  return payload?.message || trans({ id: 'staff_google_return_failed', defaultMessage: 'Google sign-in could not be completed.' })
}
